/**
 * KPI Conversation Chat (Phase 12.3)
 *
 * AI-guided conversation for generating SOPs from KPI template descriptions.
 * Stateless: sends full message history with each request.
 * Embedded in the Templates page creation modal.
 */

import { useState, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useApi } from '../../lib/hooks/useApi';
import {
  Send,
  Loader2,
  CheckCircle2,
  Bot,
  User,
  FileText,
} from 'lucide-react';

interface Message {
  role: 'ai' | 'user';
  text: string;
}

interface ConversationResponse {
  done: boolean;
  question?: string;
  questionIndex?: number;
  totalQuestions?: number;
  sopStructure?: {
    title: string;
    sections: Array<{
      title: string;
      description: string;
      sectionNumber: number;
      tasks: Array<{
        title: string;
        description: string;
        taskNumber: number;
        isSafetyCritical: boolean;
        steps: Array<{
          stepNumber: number;
          description: string;
          isSafetyCritical: boolean;
          keywords: string[];
        }>;
      }>;
    }>;
  };
  message?: string;
}

interface KpiConversationChatProps {
  templateCode: string;
  templateName: string;
  onSopGenerated: (sopStructure: ConversationResponse['sopStructure']) => void;
  onCancel: () => void;
}

export function KpiConversationChat({
  templateCode,
  templateName,
  onSopGenerated,
  onCancel,
}: KpiConversationChatProps) {
  const api = useApi();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [sopStructure, setSopStructure] = useState<ConversationResponse['sopStructure']>(undefined);
  const scrollRef = useRef<HTMLDivElement>(null);

  const conversationMutation = useMutation({
    mutationFn: (msgs: Message[]) =>
      api.post<ConversationResponse>('/api/assessment-schedules/conversation', {
        templateCode,
        messages: msgs,
      }),
  });

  // Auto-scroll to bottom
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  // Start conversation on mount — get first question
  useEffect(() => {
    startConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startConversation = async () => {
    try {
      const result = await conversationMutation.mutateAsync([]);
      if (result.question) {
        setMessages([{ role: 'ai', text: result.question }]);
      }
    } catch {
      setMessages([{ role: 'ai', text: 'Sorry, I had trouble starting the conversation. Please try again.' }]);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || conversationMutation.isPending) return;

    const userMessage: Message = { role: 'user', text: input.trim() };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');

    try {
      const result = await conversationMutation.mutateAsync(updatedMessages);

      if (result.done && result.sopStructure) {
        setIsComplete(true);
        setSopStructure(result.sopStructure);
        setMessages([...updatedMessages, {
          role: 'ai',
          text: `I've generated an SOP: "${result.sopStructure.title}" with ${result.sopStructure.sections?.length ?? 0} section(s). Click "Use This SOP" to continue.`,
        }]);
      } else if (result.question) {
        setMessages([...updatedMessages, { role: 'ai', text: result.question }]);
      }
    } catch {
      setMessages([...updatedMessages, {
        role: 'ai',
        text: 'Sorry, I had trouble processing your response. Please try again.',
      }]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-[450px]">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-guild-mint/30">
        <Bot size={16} className="text-guild-green" />
        <span className="text-sm font-semibold text-guild-green">
          AI Assistant — {templateName}
        </span>
        {conversationMutation.isPending && (
          <Loader2 size={14} className="animate-spin text-guild-green/50 ml-auto" />
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
            {msg.role === 'ai' && (
              <div className="flex-shrink-0 h-7 w-7 rounded-full bg-guild-mint/50 flex items-center justify-center">
                <Bot size={14} className="text-guild-green" />
              </div>
            )}
            <div
              className={`
                max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed
                ${msg.role === 'ai'
                  ? 'bg-gray-50 text-gray-800 rounded-tl-md'
                  : 'bg-guild-green text-white rounded-tr-md'
                }
              `}
            >
              {msg.text}
            </div>
            {msg.role === 'user' && (
              <div className="flex-shrink-0 h-7 w-7 rounded-full bg-guild-green/10 flex items-center justify-center">
                <User size={14} className="text-guild-green" />
              </div>
            )}
          </div>
        ))}

        {conversationMutation.isPending && messages.length > 0 && messages[messages.length - 1].role === 'user' && (
          <div className="flex gap-3">
            <div className="flex-shrink-0 h-7 w-7 rounded-full bg-guild-mint/50 flex items-center justify-center">
              <Bot size={14} className="text-guild-green" />
            </div>
            <div className="bg-gray-50 rounded-2xl rounded-tl-md px-4 py-3">
              <div className="flex gap-1.5">
                <div className="w-2 h-2 rounded-full bg-gray-300 animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-gray-300 animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-gray-300 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input / Complete */}
      {isComplete ? (
        <div className="px-4 py-3 border-t border-gray-100 bg-emerald-50/50">
          <div className="flex items-center gap-3">
            <CheckCircle2 size={18} className="text-emerald-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-emerald-800">{sopStructure?.title}</p>
              <p className="text-xs text-emerald-600">
                {sopStructure?.sections?.length} section(s),{' '}
                {sopStructure?.sections?.reduce((acc, s) => acc + s.tasks.length, 0)} task(s)
              </p>
            </div>
            <button
              onClick={() => onSopGenerated(sopStructure)}
              className="inline-flex items-center gap-2 rounded-lg bg-guild-green px-4 py-2 text-sm font-semibold text-white hover:bg-guild-green/90 transition-all"
            >
              <FileText size={14} />
              Use This SOP
            </button>
          </div>
        </div>
      ) : (
        <div className="px-4 py-3 border-t border-gray-100">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your answer..."
              disabled={conversationMutation.isPending}
              className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-guild-green focus:ring-1 focus:ring-guild-green/30 outline-none disabled:opacity-50"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || conversationMutation.isPending}
              className="rounded-xl bg-guild-green px-4 py-2.5 text-white hover:bg-guild-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <Send size={16} />
            </button>
          </div>
          <button
            onClick={onCancel}
            className="mt-2 text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            Cancel conversation
          </button>
        </div>
      )}
    </div>
  );
}
